function _typeof(o) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) {
    return typeof o;
  } : function (o) {
    return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
  }, _typeof(o);
}
/**
 * Purpose: SETARRAYFIELD opcode — write a field on each destination object
 * from a parallel source list (with last-value / scalar broadcasting).
 *
 * Language shape:
 *   script.destination  — context.* path to an array of objects
 *   script.source       — array, scalar, or tx collection ref
 *   script.field        — literal property name (not resolve_ref)
 *
 * destination[i][field] = source[min(i, source.len()-1)]
 *
 * Empty source arrays fail. Non-object destination elements fail.
 */

module.exports = {
  name: 'SETARRAYFIELD',
  description: "\nWrites a named field on every object in a destination array.\n\nsource may be an array, a scalar (broadcast), or a transaction collection\n(tx.from, tx.to, tx.path, tx.from.p2sh, tx.to.p2sh). When the source is\nshorter than the destination, the last source value repeats.\n\nfield is a literal property name \u2014 it is not resolved as a reference.\n",
  exampleScript: {
    op: 'SETARRAYFIELD',
    destination: 'context.constitution',
    source: '__opcodes.importarray.successors',
    field: 'owner'
  },
  execute: function execute(node, context) {
    if (!node || _typeof(node) !== 'object' || !context || _typeof(context) !== 'object') {
      return false;
    }
    var destination = node.destination;
    if (typeof destination !== 'string' || destination.length === 0) {
      return false;
    }
    if (!Object.prototype.hasOwnProperty.call(node, 'source')) {
      return false;
    }
    var field = node.field;
    if (typeof field !== 'string' || field.length === 0) {
      return false;
    }
    if (!destination.startsWith('context.')) {
      return false;
    }
    var path = destination.slice('context.'.length);
    if (!path || isForbiddenWritePath(path)) {
      return false;
    }
    var sourceValues = resolveSourceValues(node.source, context);
    if (!sourceValues || sourceValues.length === 0) {
      return false;
    }
    var dest = getContextPath(context, path);
    if (!Array.isArray(dest)) {
      return false;
    }
    if (!dest.every(function (e) {
      return e && _typeof(e) === 'object' && !Array.isArray(e);
    })) {
      return false;
    }
    var lastIdx = sourceValues.length - 1;
    for (var i = 0; i < dest.length; i += 1) {
      var srcIdx = i < sourceValues.length ? i : lastIdx;
      dest[i][field] = deepClone(sourceValues[srcIdx]);
    }
    return true;
  }
};
function isForbiddenWritePath(path) {
  return path === 'script' || path.startsWith('script.') || path === 'witness' || path.startsWith('witness.') || path === 'tx' || path.startsWith('tx.') || path === 'blk' || path.startsWith('blk.');
}
function pathSegments(path) {
  return String(path).replace(/\[/g, '.').replace(/\]/g, '').split('.').filter(function (s) {
    return s.length > 0;
  });
}
function getContextPath(root, path) {
  var segments = pathSegments(path);
  if (!segments.length) {
    return undefined;
  }
  var current = root;
  for (var i = 0; i < segments.length; i += 1) {
    var segment = segments[i];
    var asIndex = /^[0-9]+$/.test(segment) ? Number(segment) : null;
    if (asIndex !== null) {
      if (!Array.isArray(current) || asIndex >= current.length) {
        return undefined;
      }
      current = current[asIndex];
      continue;
    }
    if (!current || _typeof(current) !== 'object' || Array.isArray(current) || !Object.prototype.hasOwnProperty.call(current, segment)) {
      return undefined;
    }
    current = current[segment];
  }
  return current;
}
function isP2shSlip(slip) {
  if (!slip || _typeof(slip) !== 'object') {
    return false;
  }
  if (slip.slip_type === 'Bound' || slip.slip_type === 9) {
    return false;
  }
  var pk = slip.public_key || slip.publicKey;
  if (Array.isArray(pk) && pk[0] === 0) {
    return true;
  }
  if (pk && _typeof(pk) === 'object' && pk[0] === 0) {
    return true;
  }
  return false;
}
function resolveSpecialCollection(name, context) {
  if (!context.tx || _typeof(context.tx) !== 'object') {
    return null;
  }
  if (name === 'tx.from') {
    return Array.isArray(context.tx.from) ? context.tx.from : null;
  }
  if (name === 'tx.to') {
    return Array.isArray(context.tx.to) ? context.tx.to : null;
  }
  if (name === 'tx.path') {
    return Array.isArray(context.tx.path) ? context.tx.path : null;
  }
  if (name === 'tx.from.p2sh') {
    return Array.isArray(context.tx.from) ? context.tx.from.filter(isP2shSlip) : null;
  }
  if (name === 'tx.to.p2sh') {
    return Array.isArray(context.tx.to) ? context.tx.to.filter(isP2shSlip) : null;
  }
  return null;
}
function resolveSourceValues(source, context) {
  var resolved = resolveRef(context, source);
  if (Array.isArray(resolved)) {
    return resolved.length === 0 ? null : resolved;
  }
  if (typeof source === 'string') {
    switch (source) {
      case 'tx.from':
      case 'tx.to':
      case 'tx.path':
      case 'tx.from.p2sh':
      case 'tx.to.p2sh':
        {
          var values = resolveSpecialCollection(source, context);
          if (!values || values.length === 0) {
            return null;
          }
          return values;
        }
      default:
        break;
    }
  }
  return [resolved];
}
function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}
function resolveRef(root, ref) {
  if (typeof ref !== 'string') {
    return ref;
  }
  var parts = ref.split('.');
  var cursor = root;
  for (var i = 0; i < parts.length; i += 1) {
    var key = parts[i];
    if (!cursor || _typeof(cursor) !== 'object' || !Object.prototype.hasOwnProperty.call(cursor, key)) {
      return ref;
    }
    cursor = cursor[key];
  }
  return cursor;
}